import { useMemo } from 'react';

import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useNudgeContributorFilterQuery$data,
	useNudgeContributorFilterQuery$variables,
} from './__generated__/useNudgeContributorFilterQuery.graphql.ts';

const useNudgeContributorFilterQuery = graphql`
	query useNudgeContributorFilterQuery($contentId: ID!) {
		contentContributors(id: $contentId) {
			isCurrentUserContributor
		}
	}
`;

type UseNudgeContributorFilterResult = {
	isCurrentUserContributor: boolean;
	loading: boolean;
};

/**
 * Hook to check if the current user is a contributor to the given content.
 * Uses the contentContributors GraphQL query with the isCurrentUserContributor field.
 */
export function useNudgeContributorFilter(
	contentId: string,
	skip?: boolean,
): UseNudgeContributorFilterResult {
	const { data, loading } = useQueryInterop<
		useNudgeContributorFilterQuery$data,
		useNudgeContributorFilterQuery$variables
	>(useNudgeContributorFilterQuery, {
		variables: { contentId },
		skip: !contentId || skip,
		errorPolicy: 'all',
	});

	const isCurrentUserContributor = data?.contentContributors?.isCurrentUserContributor ?? false;

	return useMemo(
		() => ({ isCurrentUserContributor, loading }),
		[isCurrentUserContributor, loading],
	);
}
